import { Cell, Pie, PieChart, Tooltip } from 'recharts'

import { useFetchOrderGroups } from '@/hooks/features/orders/useFetchOrderGroups'
import { ORDER_STATUS_OPTIONS } from '@/constants/statuses'
import { OrderModel } from '@/types/model/Order'

import { ChartCard, ChartTooltip } from './chart-kit'

/** Status colours, in the same families as the order StatusBadge tones. */
const COLOR: Record<string, string> = {
  pending: 'hsl(var(--warning))',
  active: 'hsl(var(--success))',
  ready_to_ship: 'hsl(var(--info))',
  dispatched: 'hsl(var(--violet))',
  on_the_way: 'hsl(var(--cta))',
  delivered: 'hsl(var(--chart-1))',
  cancelled: 'hsl(var(--error))',
  returned: 'hsl(var(--grey-500))',
}

/** Donut of orders per status, from the same counts as the Orders status tabs. */
export const OrdersByStatus = ({ className }: { className?: string }) => {
  const { groups, loading } = useFetchOrderGroups()
  const rows = ORDER_STATUS_OPTIONS.map(({ label, value }) => ({
    label,
    value: Number(groups?.[`${value}_count` as keyof OrderModel.OrderGroups] ?? 0),
    color: COLOR[value] ?? 'hsl(var(--grey-400))',
  })).filter((r) => r.value > 0)
  const total = rows.reduce((s, r) => s + r.value, 0)

  return (
    <ChartCard
      title="Orders by status"
      subheader="All time"
      className={className}
      height={240}
      empty={!loading && total === 0}
      emptyText={loading ? 'Loading…' : 'No orders yet.'}
      footer={
        <ul className="grid grid-cols-2 gap-x-4 gap-y-2">
          {rows.map((r) => (
            <li key={r.label} className="flex items-center gap-2 text-sm">
              <span className="size-2.5 shrink-0 rounded-sm" style={{ background: r.color }} aria-hidden="true" />
              <span className="flex-1 truncate text-text-2">{r.label}</span>
              <span className="font-medium tabular-nums text-ink">{r.value.toLocaleString()}</span>
            </li>
          ))}
        </ul>
      }
      table={{ columns: ['Status', 'Orders'], rows: rows.map((r) => [r.label, r.value.toLocaleString()]) }}
    >
      <PieChart>
        <Tooltip content={<ChartTooltip format={(v) => `${v.toLocaleString()} orders`} />} />
        <Pie data={rows} dataKey="value" nameKey="label" innerRadius="80%" outerRadius="100%" paddingAngle={1} stroke="none">
          {rows.map((r) => (
            <Cell key={r.label} fill={r.color} />
          ))}
        </Pie>
        <text x="50%" y="46%" textAnchor="middle" className="fill-muted-foreground text-xs">
          Total
        </text>
        <text x="50%" y="58%" textAnchor="middle" className="fill-ink text-heading-lg">
          {total.toLocaleString()}
        </text>
      </PieChart>
    </ChartCard>
  )
}
